import { useReportDateState } from "../hooks/useReportDateAccess";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FaDownload, FaFileExcel, FaPrint, FaSyncAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import { API_ENDPOINTS } from "../config/constants";
import { formatStoredMoney } from "../utils/currency";
import { toLocalYmd } from "../utils/localDate";
import { downloadCSV, downloadExcel, downloadPDF, printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import ReportControlBar from "../components/ReportControlBar";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { getOfflineDiscountedOrdersReport } from "../offline/financeStore";

const today = () => toLocalYmd(new Date());

const emptyReport = (fromdate, todate) => ({
  fromdate,
  todate,
  restaurant: "",
  total_discounted_orders: 0,
  total_bill_amount: 0,
  average_discount_percent: 0,
  orders: [],
});

export default function DiscountedOrdersReportPage() {
  const [restaurantId, setRestaurantId] = useState(null);
  const [fromDate, setFromDate] = useReportDateState(today());
  const [toDate, setToDate] = useReportDateState(today());
  const [report, setReport] = useState(emptyReport(today(), today()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setRestaurantId(user.id);
  }, []);

  useEffect(() => {
    if (restaurantId) loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, fromDate, toDate]);

  const loadReport = async ({ refresh = false } = {}) => {
    if (!restaurantId) return;
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      let json;
      if (isFullOfflineModeEnabled()) {
        json = await getOfflineDiscountedOrdersReport(restaurantId, fromDate, toDate);
      } else {
        const response = await fetch(API_ENDPOINTS.REPORT_DISCOUNTED_ORDERS, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId, fromdate: fromDate, todate: toDate }),
        });
        json = await response.json();
        if (!response.ok || json?.success === false) {
          throw new Error(json?.message || "Failed to load discounted orders report");
        }
      }
      setReport({
        ...emptyReport(fromDate, toDate),
        ...json,
        orders: Array.isArray(json?.orders) ? json.orders : [],
      });
    } catch (error) {
      setReport(emptyReport(fromDate, toDate));
      toast.error(error.message || "Unable to load discounted orders report");
    } finally {
      refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const orders = useMemo(() => report.orders || [], [report.orders]);
  const hasData = orders.length > 0;
  const fmtMoney = (value) => formatStoredMoney(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const formatDateTime = (value) => (value ? new Date(value).toLocaleString("en-IN") : "-");
  const customerName = (order) => order.party_name || "Walk-in customer";

  const exportRows = () => orders.map((order, index) => [
    index + 1,
    order.ordercode || "-",
    customerName(order),
    order.party_phone || "-",
    order.table_name || "-",
    `${Number(order.discount_percent || 0)}%`,
    order.total_quantity || 0,
    order.bill_amount || 0,
    formatDateTime(order.created_at),
  ]);

  const exportHeaders = ["#", "Bill No", "Customer", "Phone", "Table", "Discount %", "Qty", "Bill Amount", "Date"];
  const rangeLabel = `${fromDate}_to_${toDate}`;

  const ensureData = () => {
    if (hasData) return true;
    toast.info("No discounted orders to export.");
    return false;
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <ReportPageHeader
        title="Discounted Orders Report"
        subtitle="All completed orders with an applied discount greater than 0%."
        stats={[
          { label: "Discounted Orders", value: report.total_discounted_orders || 0 },
          { label: "Average Discount", value: `${Number(report.average_discount_percent || 0)}%` },
          { label: "Bill Value", value: fmtMoney(report.total_bill_amount) },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker
          fromDate={fromDate}
          toDate={toDate}
          onChange={({ from, to }) => {
            if (from) setFromDate(from);
            if (to) setToDate(to);
          }}
        />
        <PrimaryBtn type="button" onClick={() => loadReport({ refresh: true })} disabled={refreshing}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading discounted orders…</Center>
        ) : !hasData ? (
          <Center>No discounted orders found for the selected date range.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Bill No.</th>
                  <th>Customer</th>
                  <th>Table</th>
                  <th>Discount</th>
                  <th>Qty</th>
                  <th>Bill Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => (
                  <tr key={order.id || `${order.ordercode}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{order.ordercode || "-"}</td>
                    <td>
                      <Customer>{customerName(order)}</Customer>
                      {order.party_phone ? <Phone>{order.party_phone}</Phone> : null}
                    </td>
                    <td>{order.table_name || "-"}</td>
                    <td><DiscountBadge>{Number(order.discount_percent || 0)}%</DiscountBadge></td>
                    <td>{order.total_quantity || 0}</td>
                    <td>{fmtMoney(order.bill_amount)}</td>
                    <td>{formatDateTime(order.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}

        <ActionRow data-export-ignore="true">
          <PrimaryBtn type="button" onClick={() => ensureData() && downloadExcel(`discounted_orders_${rangeLabel}.xlsx`, exportHeaders, exportRows())}>
            <FaFileExcel /> Excel
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={() => ensureData() && downloadCSV(`discounted_orders_${rangeLabel}.csv`, exportHeaders, exportRows())}>
            CSV
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={() => ensureData() && downloadPDF(reportRef.current, `discounted_orders_${rangeLabel}.pdf`)}>
            <FaDownload /> PDF
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={() => ensureData() && printThermal(reportRef.current, `discounted_orders_${rangeLabel}`)}>
            <FaPrint /> Thermal Print
          </PrimaryBtn><PrimaryBtn type="button" onClick={() => ensureData() && printThermalViaUsb(reportRef.current, `discounted_orders_${rangeLabel}`)}>
            <FaPrint /> Thermal Print via USB
          </PrimaryBtn>
        </ActionRow>
      </Card>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  padding: 18px;
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
`;

const Card = styled.div`
  padding: 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.05);
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 8px 14px;
  border: 0;
  border-radius: 10px;
  color: #ffffff;
  background: linear-gradient(135deg, var(--app-primary), var(--app-primary-hover));
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(7, 95, 158, 0.15);

  &:disabled { opacity: 0.65; cursor: wait; }
  &:focus-visible { outline: 3px solid rgba(7, 95, 158, 0.35); outline-offset: 2px; }
`;

const Center = styled.div`
  padding: 32px 0;
  color: #64748b;
  text-align: center;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;

  th, td { padding: 12px 14px; border-bottom: 1px solid #e2e8f0; text-align: left; font-size: 14px; }
  th { background: #f8fafc; color: #334155; font-size: 0.75rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
  tbody tr:last-child td { border-bottom: 0; }
  tbody tr:hover td { background: #f8fafc; }
`;

const Customer = styled.div`font-weight: 700; color: #0f172a;`;
const Phone = styled.div`margin-top: 3px; color: #64748b; font-size: 12px;`;
const DiscountBadge = styled.span`
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 999px;
  color: #9a3412;
  background: #ffedd5;
  font-size: 12px;
  font-weight: 800;
`;
const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
`;
